import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/lib/auth";
import { LangProvider } from "@/lib/lang";
import { ThemeProvider } from "@/lib/theme";
import AuthCallback from "@/pages/AuthCallback";
import AdminPage from "@/pages/AdminPage";
import { Preloader } from "@/components/landing/Preloader";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { Problems } from "@/components/landing/Problems";
import { Solutions } from "@/components/landing/Solutions";
import { Pricing } from "@/components/landing/Pricing";
import { Calculator } from "@/components/landing/Calculator";
import { Payment } from "@/components/landing/Payment";
import { Workflow } from "@/components/landing/Workflow";
import { Showcase } from "@/components/landing/Showcase";
import { Blog } from "@/components/landing/Blog";
import { Faq } from "@/components/landing/Faq";
import { CtaForm } from "@/components/landing/CtaForm";
import { Footer } from "@/components/landing/Footer";
import { FloatingWa } from "@/components/landing/FloatingWa";
import { MobileBottomBar } from "@/components/landing/MobileBottomBar";
import { EmberCanvas } from "@/components/landing/EmberCanvas";
import { ClickSparks } from "@/components/landing/ClickSparks";
import ArticlePage from "@/pages/ArticlePage";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const Landing = () => (
    <main>
        <Hero />
        <Marquee />
        <Problems />
        <Solutions />
        <Pricing />
        <Calculator />
        <Payment />
        <Workflow />
        <Showcase />
        <Blog />
        <Faq />
        <CtaForm />
    </main>
);

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/tips/:slug" element={<ArticlePage />} />
            <Route path="/admin" element={<AdminPage />} />
        </Routes>
    );
}

const Shell = () => {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    return (
        <>
            <EmberCanvas fixed density={0.28} testid="ember-canvas-global" />
            <ClickSparks />
            {!isAdmin && <Navbar />}
            <div className={`relative z-10 ${isAdmin ? "" : "pb-24 md:pb-0"}`}>
                <AppRouter />
                {!isAdmin && <Footer />}
            </div>
            {!isAdmin && <FloatingWa />}
            {!isAdmin && <MobileBottomBar />}
        </>
    );
};

function App() {
    const [booted, setBooted] = useState(
        () => sessionStorage.getItem("tf_booted") === "1"
    );

    useEffect(() => {
        if (booted) return;
        const t = setTimeout(() => {
            sessionStorage.setItem("tf_booted", "1");
            setBooted(true);
        }, 2100);
        return () => clearTimeout(t);
    }, [booted]);

    useEffect(() => {
        if (!booted) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        const onClick = (e) => {
            const anchor = e.target.closest('a[href^="/#"]');
            if (!anchor || window.location.pathname !== "/") return;
            const el = document.querySelector(anchor.getAttribute("href").slice(1));
            if (!el) return;
            e.preventDefault();
            lenis.scrollTo(el, { offset: -72 });
        };
        document.addEventListener("click", onClick);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
            document.removeEventListener("click", onClick);
        };
    }, [booted]);

    return (
        <BrowserRouter>
            <ThemeProvider>
            <LangProvider>
                <AuthProvider>
                <ScrollToTop />
                <div className="page-shell min-h-screen">
                    <AnimatePresence>{!booted && <Preloader />}</AnimatePresence>
                    {booted && <Shell />}
                </div>
                </AuthProvider>
            </LangProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
